import React, { useState, useCallback, useRef } from 'react';
import OrbitLayout from '../components/OrbitLayout';
import { useLanguage } from '../context/LanguageContext';
import { CurrencyDollar, FilePdf, Play, Warning, CheckCircle, ArrowDown, ArrowUp, CaretDown, CaretRight, ArrowsClockwise } from '@phosphor-icons/react';
import { formatMoney } from '../utils/formatMoney';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const OrbitCashFlowPage = () => {
  const { language } = useLanguage();
  const t = (fr, en) => language === 'fr' ? fr : en;
  const cashFlowRef = useRef(null);

  // Financing inputs (editable per year)
  const [financingInputs, setFinancingInputs] = useState({
    capitalInjection: 0,
    loansReceived: 0,
    loanRepayments: 0,
    dividends: 0,
  });

  const [results, setResults] = useState(null);
  const [calculated, setCalculated] = useState(false);
  const [expandedYear, setExpandedYear] = useState(null);
  const [simData, setSimData] = useState(null);
  const [expData, setExpData] = useState(null);
  const [dataStatus, setDataStatus] = useState({ sim: false, exp: false });

  const loadData = useCallback(() => {
    let sim = null, exp = null;
    try { sim = JSON.parse(localStorage.getItem('orbit_simulator_data')); } catch (e) { console.error('Parse sim data:', e); }
    try { exp = JSON.parse(localStorage.getItem('orbit_expenses_data')); } catch (e) { console.error('Parse exp data:', e); }
    setSimData(sim);
    setExpData(exp);
    setDataStatus({ sim: !!sim, exp: !!exp });
    return { sim, exp };
  }, []);

  // Load on first render
  useState(() => { loadData(); });

  const generate = useCallback(() => {
    const { sim, exp } = loadData();
    if (!sim || !exp) return;

    const years = sim.annuals.length;
    const annualCF = [];

    for (let y = 0; y < years; y++) {
      const yr = sim.annuals[y];
      const monthlyRevenue = yr.totalRev / 12;

      // Compute expenses based on stored raw data, applying % to actual revenue
      let operatingExpenses = 0;
      let marketingExpenses = 0;
      const opDetails = [];
      const mktDetails = [];

      if (exp.rawCategories) {
        exp.rawCategories.forEach(cat => {
          let catTotal = 0;
          const items = cat.items.map(item => {
            let monthly;
            if (item.type === 'flat') {
              monthly = item.value;
            } else if (item.type === 'percent') {
              monthly = monthlyRevenue * item.value / 100;
            } else {
              monthly = monthlyRevenue * 0.3 * item.value / 100;
            }
            catTotal += monthly;
            return { ...item, monthly, annual: monthly * 12 };
          });

          const entry = { id: cat.id, items, monthlyTotal: catTotal, annualTotal: catTotal * 12 };
          if (cat.section === 'marketing') {
            marketingExpenses += catTotal * 12;
            mktDetails.push(entry);
          } else {
            operatingExpenses += catTotal * 12;
            opDetails.push(entry);
          }
        });
      }

      // Operating Activities
      const cashInOperating = yr.totalRev;
      const cashOutCommissions = yr.totalComm;
      const cashOutOperating = operatingExpenses;
      const netOperating = cashInOperating - cashOutCommissions - cashOutOperating;

      // Sales & Marketing Activities
      const netMarketing = -marketingExpenses;

      // Financing Activities (same each year for simplicity)
      const cashInFinancing = financingInputs.capitalInjection + financingInputs.loansReceived;
      const cashOutFinancing = financingInputs.loanRepayments + financingInputs.dividends;
      const netFinancing = cashInFinancing - cashOutFinancing;

      // Net Cash Flow
      const netCashFlow = netOperating + netMarketing + netFinancing;
      const prevCumul = y > 0 ? annualCF[y - 1].cumulativeCash : 0;

      annualCF.push({
        year: y + 1,
        startMembers: yr.startMembers,
        endMembers: yr.endMembers,
        // Operating
        cashInOperating,
        membershipRev: yr.totalMembershipRev,
        affiliateRev: yr.totalAffiliateRev,
        cashOutCommissions,
        matrixComm: yr.matrixComm,
        fsComm: yr.fsComm,
        matchComm: yr.matchComm,
        cashOutOperating,
        opDetails,
        netOperating,
        // Marketing
        marketingExpenses,
        mktDetails,
        netMarketing,
        // Financing
        cashInFinancing,
        cashOutFinancing,
        netFinancing,
        // Totals
        netCashFlow,
        cumulativeCash: prevCumul + netCashFlow,
        // Reserve info
        reserve: yr.totalReserve,
      });
    }

    // Grand totals
    const totalRevenue = annualCF.reduce((s, y) => s + y.cashInOperating, 0);
    const totalCommissions = annualCF.reduce((s, y) => s + y.cashOutCommissions, 0);
    const totalOpExpenses = annualCF.reduce((s, y) => s + y.cashOutOperating, 0);
    const totalMktExpenses = annualCF.reduce((s, y) => s + y.marketingExpenses, 0);
    const totalFinancing = annualCF.reduce((s, y) => s + y.netFinancing, 0);
    const totalNetCash = annualCF.reduce((s, y) => s + y.netCashFlow, 0);

    setResults({
      annualCF,
      totalRevenue,
      totalCommissions,
      totalOpExpenses,
      totalMktExpenses,
      totalFinancing,
      totalNetCash,
      projectionYears: years,
      matrixLabel: sim.matrixLabel,
    });
    setCalculated(true);
  }, [loadData, financingInputs]);

  // PDF Export
  const exportPDF = useCallback(() => {
    if (!results) return;
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageW = doc.internal.pageSize.getWidth();

    // Header
    doc.setFontSize(18);
    doc.setTextColor(40, 40, 40);
    doc.text('Cash Flow Statement', pageW / 2, 20, { align: 'center' });
    doc.setFontSize(10);
    doc.setTextColor(120, 120, 120);
    doc.text(`Orbit Matrix ${results.matrixLabel} | ${t('Projection', 'Projection')} ${results.projectionYears} ${t('an(s)', 'year(s)')}`, pageW / 2, 27, { align: 'center' });
    doc.text(`${t('Genere le', 'Generated on')} ${new Date().toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-US')}`, pageW / 2, 32, { align: 'center' });

    let startY = 40;

    results.annualCF.forEach((yr, idx) => {
      // Check if we need a new page
      if (startY > 230) {
        doc.addPage();
        startY = 20;
      }

      // Year title
      doc.setFontSize(13);
      doc.setTextColor(40, 40, 40);
      doc.text(`${t('Annee', 'Year')} ${yr.year}`, 14, startY);
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text(`${yr.startMembers.toLocaleString()} → ${yr.endMembers.toLocaleString()} ${t('membres', 'members')}`, 50, startY);
      startY += 4;

      const fmtNum = (n) => {
        const sign = n < 0 ? '-' : '';
        const abs = Math.abs(Math.round(n));
        return sign + abs.toLocaleString() + ' $';
      };

      // Operating Activities Table
      const opRows = [
        [{ content: t('FLUX D\'EXPLOITATION', 'OPERATING ACTIVITIES'), colSpan: 2, styles: { fontStyle: 'bold', fillColor: [230, 245, 230] } }],
        [t('  Revenus abonnements (mensuel)', '  Membership Revenue (monthly)'), fmtNum(yr.membershipRev)],
        [t('  Revenus affiliations (unique)', '  Affiliate Revenue (one-time)'), fmtNum(yr.affiliateRev)],
        [{ content: t('  Total entrees', '  Total Cash In'), styles: { fontStyle: 'bold' } }, { content: fmtNum(yr.cashInOperating), styles: { fontStyle: 'bold' } }],
        ['', ''],
        [t('  Commissions Matrice', '  Matrix Commissions'), '(' + fmtNum(yr.matrixComm) + ')'],
        [t('  Fast Start', '  Fast Start'), '(' + fmtNum(yr.fsComm) + ')'],
        [t('  Matching Bonus', '  Matching Bonus'), '(' + fmtNum(yr.matchComm) + ')'],
      ];

      yr.opDetails.forEach(cat => {
        const catLabel = language === 'fr'
          ? (cat.id === 'admin' ? 'Admin & Gestion' : cat.id === 'tech' ? 'Technologie' : cat.id === 'financial' ? 'Frais financiers' : cat.id === 'legal' ? 'Juridique' : cat.id === 'training' ? 'Formation' : cat.id === 'reserves' ? 'Reserves' : cat.id)
          : (cat.id === 'admin' ? 'Admin & Mgmt' : cat.id === 'tech' ? 'Technology' : cat.id === 'financial' ? 'Financial' : cat.id === 'legal' ? 'Legal' : cat.id === 'training' ? 'Training' : cat.id === 'reserves' ? 'Reserves' : cat.id);
        opRows.push([`  ${catLabel}`, '(' + fmtNum(cat.annualTotal) + ')']);
      });

      opRows.push([
        { content: t('NET FLUX D\'EXPLOITATION', 'NET OPERATING CASH FLOW'), styles: { fontStyle: 'bold', fillColor: yr.netOperating >= 0 ? [220, 255, 220] : [255, 220, 220] } },
        { content: fmtNum(yr.netOperating), styles: { fontStyle: 'bold', halign: 'right' } },
      ]);

      // Sales & Marketing
      const mktRows = [
        [{ content: t('VENTES & MARKETING', 'SALES & MARKETING'), colSpan: 2, styles: { fontStyle: 'bold', fillColor: [255, 240, 220] } }],
      ];
      yr.mktDetails.forEach(cat => {
        cat.items.forEach(item => {
          mktRows.push([`  ${language === 'fr' ? item.fr : item.en}`, '(' + fmtNum(item.annual) + ')']);
        });
      });
      mktRows.push([
        { content: t('NET VENTES & MARKETING', 'NET SALES & MARKETING'), styles: { fontStyle: 'bold' } },
        { content: fmtNum(yr.netMarketing), styles: { fontStyle: 'bold', halign: 'right' } },
      ]);

      // Financing
      const finRows = [
        [{ content: t('FINANCEMENT', 'FINANCING ACTIVITIES'), colSpan: 2, styles: { fontStyle: 'bold', fillColor: [220, 230, 255] } }],
      ];
      if (yr.cashInFinancing > 0) {
        if (financingInputs.capitalInjection > 0) finRows.push([t('  Injection de capital', '  Capital Injection'), fmtNum(financingInputs.capitalInjection)]);
        if (financingInputs.loansReceived > 0) finRows.push([t('  Prets recus', '  Loans Received'), fmtNum(financingInputs.loansReceived)]);
      }
      if (yr.cashOutFinancing > 0) {
        if (financingInputs.loanRepayments > 0) finRows.push([t('  Remboursement prets', '  Loan Repayments'), '(' + fmtNum(financingInputs.loanRepayments) + ')']);
        if (financingInputs.dividends > 0) finRows.push([t('  Dividendes', '  Dividends'), '(' + fmtNum(financingInputs.dividends) + ')']);
      }
      if (yr.cashInFinancing === 0 && yr.cashOutFinancing === 0) {
        finRows.push([t('  Aucune activite', '  No activity'), '—']);
      }
      finRows.push([
        { content: t('NET FINANCEMENT', 'NET FINANCING'), styles: { fontStyle: 'bold' } },
        { content: fmtNum(yr.netFinancing), styles: { fontStyle: 'bold', halign: 'right' } },
      ]);

      // Net Cash Flow
      const totalRows = [
        [{ content: '', colSpan: 2 }],
        [
          { content: t('FLUX DE TRESORERIE NET', 'NET CASH FLOW'), styles: { fontStyle: 'bold', fontSize: 10, fillColor: yr.netCashFlow >= 0 ? [200, 255, 200] : [255, 200, 200] } },
          { content: fmtNum(yr.netCashFlow), styles: { fontStyle: 'bold', fontSize: 10, halign: 'right', fillColor: yr.netCashFlow >= 0 ? [200, 255, 200] : [255, 200, 200] } },
        ],
        [
          { content: t('Position de tresorerie cumulee', 'Cumulative Cash Position'), styles: { fontStyle: 'italic' } },
          { content: fmtNum(yr.cumulativeCash), styles: { fontStyle: 'bold', halign: 'right' } },
        ],
      ];

      const allRows = [...opRows, ...mktRows, ...finRows, ...totalRows];

      autoTable(doc, {
        startY,
        body: allRows,
        theme: 'grid',
        styles: { fontSize: 8, cellPadding: 1.5 },
        columnStyles: {
          0: { cellWidth: 120 },
          1: { cellWidth: 50, halign: 'right' },
        },
        margin: { left: 14, right: 14 },
      });

      startY = doc.lastAutoTable.finalY + 10;
    });

    // Grand Total page
    if (startY > 200) { doc.addPage(); startY = 20; }

    doc.setFontSize(14);
    doc.setTextColor(40, 40, 40);
    doc.text(t('RESUME GLOBAL', 'GLOBAL SUMMARY'), pageW / 2, startY, { align: 'center' });
    startY += 5;

    const fmtNum = (n) => {
      const sign = n < 0 ? '-' : '';
      const abs = Math.abs(Math.round(n));
      return sign + abs.toLocaleString() + ' $';
    };

    autoTable(doc, {
      startY,
      body: [
        [t('Total Revenus', 'Total Revenue'), fmtNum(results.totalRevenue)],
        [t('Total Commissions', 'Total Commissions'), '(' + fmtNum(results.totalCommissions) + ')'],
        [t('Total Depenses Exploitation', 'Total Operating Expenses'), '(' + fmtNum(results.totalOpExpenses) + ')'],
        [t('Total Ventes & Marketing', 'Total Sales & Marketing'), '(' + fmtNum(results.totalMktExpenses) + ')'],
        [t('Total Financement', 'Total Financing'), fmtNum(results.totalFinancing)],
        [{ content: '', colSpan: 2 }],
        [
          { content: t('FLUX DE TRESORERIE NET TOTAL', 'TOTAL NET CASH FLOW'), styles: { fontStyle: 'bold', fontSize: 10, fillColor: results.totalNetCash >= 0 ? [200, 255, 200] : [255, 200, 200] } },
          { content: fmtNum(results.totalNetCash), styles: { fontStyle: 'bold', fontSize: 10, halign: 'right', fillColor: results.totalNetCash >= 0 ? [200, 255, 200] : [255, 200, 200] } },
        ],
      ],
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 2 },
      columnStyles: {
        0: { cellWidth: 120 },
        1: { cellWidth: 50, halign: 'right' },
      },
      margin: { left: 14, right: 14 },
    });

    doc.save(`CashFlow_Orbit_${results.projectionYears}yr_${new Date().toISOString().slice(0, 10)}.pdf`);
  }, [results, language, t, financingInputs]);

  const CATEGORY_LABELS_FR = { admin: 'Admin & Gestion', tech: 'Technologie', financial: 'Frais financiers', legal: 'Juridique', training: 'Formation', reserves: 'Reserves' };
  const CATEGORY_LABELS_EN = { admin: 'Admin & Mgmt', tech: 'Technology', financial: 'Financial', legal: 'Legal', training: 'Training', reserves: 'Reserves' };

  return (
    <OrbitLayout>
      <div className="space-y-6" data-testid="orbit-cashflow-page" ref={cashFlowRef}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
              <CurrencyDollar className="w-8 h-8 text-emerald-400" />
              Cash Flow Statement
            </h1>
            <p className="text-gray-300 text-sm mt-1">{t('Etat des flux de tresorerie - Combine simulateur + depenses', 'Statement of Cash Flows - Combines simulator + expenses')}</p>
          </div>
          {calculated && results && (
            <button onClick={exportPDF}
              className="flex items-center gap-2 px-5 py-2.5 bg-red-600 hover:bg-red-500 text-white font-medium rounded-lg transition-colors shadow-lg"
              data-testid="cf-export-pdf">
              <FilePdf className="w-5 h-5" />
              {t('Exporter PDF', 'Export PDF')}
            </button>
          )}
        </div>

        {/* Data Source Status */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className={`rounded-xl p-4 border flex items-center gap-3 ${dataStatus.sim ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-red-500/10 border-red-500/20'}`}>
            {dataStatus.sim ? <CheckCircle className="w-5 h-5 text-emerald-400" weight="fill" /> : <Warning className="w-5 h-5 text-red-400" />}
            <div>
              <p className={`text-sm font-medium ${dataStatus.sim ? 'text-emerald-400' : 'text-red-400'}`}>
                {t('Simulateur de Matrice', 'Matrix Simulator')}
              </p>
              <p className="text-xs text-gray-500">
                {dataStatus.sim
                  ? `${simData?.matrixLabel} | ${simData?.projectionYears} ${t('an(s)', 'yr(s)')} | ${new Date(simData?.timestamp).toLocaleString(language === 'fr' ? 'fr-CA' : 'en-US')}`
                  : t('Aucune donnee - Lancez le Simulateur d\'abord', 'No data - Run the Simulator first')}
              </p>
            </div>
          </div>
          <div className={`rounded-xl p-4 border flex items-center gap-3 ${dataStatus.exp ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-red-500/10 border-red-500/20'}`}>
            {dataStatus.exp ? <CheckCircle className="w-5 h-5 text-emerald-400" weight="fill" /> : <Warning className="w-5 h-5 text-red-400" />}
            <div>
              <p className={`text-sm font-medium ${dataStatus.exp ? 'text-emerald-400' : 'text-red-400'}`}>
                {t('Postes de depenses', 'Expense Categories')}
              </p>
              <p className="text-xs text-gray-500">
                {dataStatus.exp
                  ? `${formatMoney(expData?.totalMonthly, language, 0)}/${t('mois', 'mo')} | ${new Date(expData?.timestamp).toLocaleString(language === 'fr' ? 'fr-CA' : 'en-US')}`
                  : t('Aucune donnee - Configurez les Depenses d\'abord', 'No data - Configure Expenses first')}
              </p>
            </div>
          </div>
        </div>

        {/* Financing Inputs */}
        <div className="bg-[#111827]/80 border border-blue-500/20 rounded-xl p-5">
          <h2 className="text-sm font-semibold text-blue-400 mb-3">{t('Activites de financement (par annee)', 'Financing Activities (per year)')}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-[10px] text-gray-400 mb-1">{t('Injection capital', 'Capital Injection')}</label>
              <div className="relative">
                <input type="number" min="0" value={financingInputs.capitalInjection}
                  onChange={e => setFinancingInputs(p => ({ ...p, capitalInjection: Number(e.target.value) }))}
                  className="w-full px-2 py-2 bg-[#1E293B] border border-blue-500/20 rounded-lg text-white text-sm pr-7"
                  data-testid="cf-capital" />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-[10px]">$/yr</span>
              </div>
            </div>
            <div>
              <label className="block text-[10px] text-gray-400 mb-1">{t('Prets recus', 'Loans Received')}</label>
              <div className="relative">
                <input type="number" min="0" value={financingInputs.loansReceived}
                  onChange={e => setFinancingInputs(p => ({ ...p, loansReceived: Number(e.target.value) }))}
                  className="w-full px-2 py-2 bg-[#1E293B] border border-blue-500/20 rounded-lg text-white text-sm pr-7"
                  data-testid="cf-loans" />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-[10px]">$/yr</span>
              </div>
            </div>
            <div>
              <label className="block text-[10px] text-gray-400 mb-1">{t('Remboursement prets', 'Loan Repayments')}</label>
              <div className="relative">
                <input type="number" min="0" value={financingInputs.loanRepayments}
                  onChange={e => setFinancingInputs(p => ({ ...p, loanRepayments: Number(e.target.value) }))}
                  className="w-full px-2 py-2 bg-[#1E293B] border border-blue-500/20 rounded-lg text-white text-sm pr-7"
                  data-testid="cf-repay" />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-[10px]">$/yr</span>
              </div>
            </div>
            <div>
              <label className="block text-[10px] text-gray-400 mb-1">{t('Dividendes', 'Dividends')}</label>
              <div className="relative">
                <input type="number" min="0" value={financingInputs.dividends}
                  onChange={e => setFinancingInputs(p => ({ ...p, dividends: Number(e.target.value) }))}
                  className="w-full px-2 py-2 bg-[#1E293B] border border-blue-500/20 rounded-lg text-white text-sm pr-7"
                  data-testid="cf-dividends" />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-[10px]">$/yr</span>
              </div>
            </div>
          </div>
        </div>

        {/* Generate & Refresh */}
        <div className="flex gap-3">
          <button onClick={generate} disabled={!dataStatus.sim || !dataStatus.exp}
            className={`flex-1 py-4 font-bold rounded-xl flex items-center justify-center gap-2 text-lg transition-all shadow-lg ${
              dataStatus.sim && dataStatus.exp
                ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-400 hover:to-teal-400 shadow-emerald-500/25'
                : 'bg-gray-700 text-gray-400 cursor-not-allowed'}`}
            data-testid="cf-generate-btn">
            <Play className="w-6 h-6" weight="fill" />
            {t('Generer le Cash Flow', 'Generate Cash Flow')}
          </button>
          <button onClick={loadData}
            className="px-4 py-4 bg-[#1E293B] border border-indigo-500/20 text-indigo-400 font-medium rounded-xl hover:bg-indigo-500/10 transition-colors"
            data-testid="cf-refresh-btn" title={t('Recharger les donnees', 'Reload data')}>
            <ArrowsClockwise className="w-6 h-6" />
          </button>
        </div>

        {/* RESULTS */}
        {calculated && results && (
        <div className="space-y-5">

          {/* Summary Banner */}
          <div className={`rounded-xl p-5 border ${results.totalNetCash >= 0 ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-red-500/10 border-red-500/20'}`}>
            <div className="flex items-start gap-4">
              {results.totalNetCash >= 0
                ? <CheckCircle className="w-8 h-8 text-emerald-400 flex-shrink-0" weight="fill" />
                : <Warning className="w-8 h-8 text-red-400 flex-shrink-0" />}
              <div className="flex-1">
                <h3 className={`text-lg font-bold ${results.totalNetCash >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  Cash Flow {results.totalNetCash >= 0 ? t('POSITIF', 'POSITIVE') : t('NEGATIF', 'NEGATIVE')}
                </h3>
                <p className="text-gray-400 text-sm">{results.matrixLabel} | {results.projectionYears} {t('an(s)', 'year(s)')}</p>
              </div>
              <div className="text-right">
                <p className={`text-3xl font-bold ${results.totalNetCash >= 0 ? 'text-emerald-400' : 'text-red-400'}`}
                  data-testid="cf-total-net">
                  {formatMoney(results.totalNetCash, language, 0)}
                </p>
                <p className="text-xs text-gray-500">{t('Flux net cumule', 'Cumulative net flow')}</p>
              </div>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="bg-[#111827]/80 border border-emerald-500/20 rounded-xl p-3 text-center">
              <p className="text-[9px] text-gray-400 uppercase">{t('Revenus', 'Revenue')}</p>
              <p className="text-lg font-bold text-emerald-400">{formatMoney(results.totalRevenue, language, 0)}</p>
            </div>
            <div className="bg-[#111827]/80 border border-red-500/20 rounded-xl p-3 text-center">
              <p className="text-[9px] text-gray-400 uppercase">{t('Commissions', 'Commissions')}</p>
              <p className="text-lg font-bold text-red-400">{formatMoney(results.totalCommissions, language, 0)}</p>
            </div>
            <div className="bg-[#111827]/80 border border-indigo-500/20 rounded-xl p-3 text-center">
              <p className="text-[9px] text-gray-400 uppercase">{t('Exploitation', 'Operating')}</p>
              <p className="text-lg font-bold text-indigo-400">{formatMoney(results.totalOpExpenses, language, 0)}</p>
            </div>
            <div className="bg-[#111827]/80 border border-orange-500/20 rounded-xl p-3 text-center">
              <p className="text-[9px] text-gray-400 uppercase">S&M</p>
              <p className="text-lg font-bold text-orange-400">{formatMoney(results.totalMktExpenses, language, 0)}</p>
            </div>
            <div className="bg-[#111827]/80 border border-blue-500/20 rounded-xl p-3 text-center">
              <p className="text-[9px] text-gray-400 uppercase">{t('Financement', 'Financing')}</p>
              <p className="text-lg font-bold text-blue-400">{formatMoney(results.totalFinancing, language, 0)}</p>
            </div>
          </div>

          {/* Annual Cash Flow Cards */}
          {results.annualCF.map(yr => {
            const isExpanded = expandedYear === yr.year;
            return (
            <div key={yr.year} className="bg-[#111827]/80 border border-indigo-500/10 rounded-xl overflow-hidden">
              <button onClick={() => setExpandedYear(isExpanded ? null : yr.year)}
                className="w-full p-5 hover:bg-white/5 transition-colors"
                data-testid={`cf-year-${yr.year}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold text-lg ${yr.netCashFlow >= 0 ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>
                      {t('A', 'Y')}{yr.year}
                    </div>
                    <div className="text-left">
                      <h3 className="text-white font-semibold">{t('Annee', 'Year')} {yr.year}</h3>
                      <p className="text-gray-500 text-xs">{yr.startMembers.toLocaleString()} → {yr.endMembers.toLocaleString()} {t('membres', 'members')}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right hidden sm:block">
                      <p className="text-emerald-400 text-sm">{formatMoney(yr.netOperating, language, 0)}</p>
                      <p className="text-[10px] text-gray-400 uppercase">{t('Exploitation', 'Operating')}</p>
                    </div>
                    <div className="text-right hidden sm:block">
                      <p className="text-orange-400 text-sm">{formatMoney(yr.netMarketing, language, 0)}</p>
                      <p className="text-[10px] text-gray-400 uppercase">S&M</p>
                    </div>
                    <div className="text-right">
                      <p className={`text-lg font-bold ${yr.netCashFlow >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {formatMoney(yr.netCashFlow, language, 0)}
                      </p>
                      <p className="text-[10px] text-gray-400">{t('Cumul', 'Cumul.')}: {formatMoney(yr.cumulativeCash, language, 0)}</p>
                    </div>
                    {isExpanded ? <CaretDown className="w-5 h-5 text-gray-400" /> : <CaretRight className="w-5 h-5 text-gray-400" />}
                  </div>
                </div>
              </button>

              {isExpanded && (
              <div className="border-t border-indigo-500/10 p-5 space-y-4">

                {/* SECTION 1: Operating Activities */}
                <div className="bg-emerald-500/5 border border-emerald-500/10 rounded-lg p-4 space-y-2">
                  <h4 className="text-sm font-semibold text-emerald-400 uppercase tracking-wider">{t('Flux d\'exploitation', 'Operating Activities')}</h4>
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-300 flex items-center gap-1.5"><ArrowDown className="w-3 h-3 text-emerald-400" />{t('Revenus abonnements', 'Membership Revenue')} <span className="text-[10px] text-indigo-400">{t('mensuel', 'monthly')}</span></span>
                      <span className="text-emerald-400">{formatMoney(yr.membershipRev, language, 0)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-300 flex items-center gap-1.5"><ArrowDown className="w-3 h-3 text-emerald-400" />{t('Revenus affiliations', 'Affiliate Revenue')} <span className="text-[10px] text-amber-400">{t('unique', 'one-time')}</span></span>
                      <span className="text-emerald-400">{formatMoney(yr.affiliateRev, language, 0)}</span>
                    </div>
                    <div className="border-t border-emerald-500/10 pt-1.5 flex justify-between text-sm font-medium">
                      <span className="text-white">{t('Total entrees', 'Total Cash In')}</span>
                      <span className="text-emerald-400 font-bold">{formatMoney(yr.cashInOperating, language, 0)}</span>
                    </div>
                  </div>

                  <div className="mt-3 space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400 flex items-center gap-1.5"><ArrowUp className="w-3 h-3 text-red-400" />{t('Commissions Matrice', 'Matrix Commissions')}</span>
                      <span className="text-red-400">({formatMoney(yr.matrixComm, language, 0)})</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400 flex items-center gap-1.5"><ArrowUp className="w-3 h-3 text-red-400" />Fast Start</span>
                      <span className="text-red-400">({formatMoney(yr.fsComm, language, 0)})</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400 flex items-center gap-1.5"><ArrowUp className="w-3 h-3 text-red-400" />Matching Bonus</span>
                      <span className="text-red-400">({formatMoney(yr.matchComm, language, 0)})</span>
                    </div>
                    {yr.opDetails.map(cat => (
                      <div key={cat.id} className="flex justify-between text-sm">
                        <span className="text-gray-400 flex items-center gap-1.5">
                          <ArrowUp className="w-3 h-3 text-red-400" />
                          {language === 'fr' ? (CATEGORY_LABELS_FR[cat.id] || cat.id) : (CATEGORY_LABELS_EN[cat.id] || cat.id)}
                        </span>
                        <span className="text-red-400">({formatMoney(cat.annualTotal, language, 0)})</span>
                      </div>
                    ))}
                  </div>

                  <div className="border-t-2 border-emerald-500/20 pt-2 flex justify-between font-bold">
                    <span className={yr.netOperating >= 0 ? 'text-emerald-400' : 'text-red-400'}>{t('Net Exploitation', 'Net Operating')}</span>
                    <span className={yr.netOperating >= 0 ? 'text-emerald-400' : 'text-red-400'}>{formatMoney(yr.netOperating, language, 0)}</span>
                  </div>
                </div>

                {/* SECTION 2: Sales & Marketing */}
                <div className="bg-orange-500/5 border border-orange-500/10 rounded-lg p-4 space-y-2">
                  <h4 className="text-sm font-semibold text-orange-400 uppercase tracking-wider">{t('Ventes & Marketing', 'Sales & Marketing')}</h4>
                  {yr.mktDetails.map(cat =>
                    cat.items.map(item => (
                      <div key={item.id} className="flex justify-between text-sm">
                        <span className="text-gray-400 flex items-center gap-1.5">
                          <ArrowUp className="w-3 h-3 text-orange-400" />
                          {language === 'fr' ? item.fr : item.en}
                        </span>
                        <span className="text-orange-400">({formatMoney(item.annual, language, 0)})</span>
                      </div>
                    ))
                  )}
                  <div className="border-t-2 border-orange-500/20 pt-2 flex justify-between font-bold">
                    <span className="text-orange-400">{t('Net Ventes & Marketing', 'Net Sales & Marketing')}</span>
                    <span className="text-orange-400">{formatMoney(yr.netMarketing, language, 0)}</span>
                  </div>
                </div>

                {/* SECTION 3: Financing */}
                <div className="bg-blue-500/5 border border-blue-500/10 rounded-lg p-4 space-y-2">
                  <h4 className="text-sm font-semibold text-blue-400 uppercase tracking-wider">{t('Financement', 'Financing Activities')}</h4>
                  {financingInputs.capitalInjection > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-300">{t('Injection de capital', 'Capital Injection')}</span>
                      <span className="text-blue-400">{formatMoney(financingInputs.capitalInjection, language, 0)}</span>
                    </div>
                  )}
                  {financingInputs.loansReceived > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-300">{t('Prets recus', 'Loans Received')}</span>
                      <span className="text-blue-400">{formatMoney(financingInputs.loansReceived, language, 0)}</span>
                    </div>
                  )}
                  {financingInputs.loanRepayments > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">{t('Remboursement prets', 'Loan Repayments')}</span>
                      <span className="text-red-400">({formatMoney(financingInputs.loanRepayments, language, 0)})</span>
                    </div>
                  )}
                  {financingInputs.dividends > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">{t('Dividendes', 'Dividends')}</span>
                      <span className="text-red-400">({formatMoney(financingInputs.dividends, language, 0)})</span>
                    </div>
                  )}
                  {yr.cashInFinancing === 0 && yr.cashOutFinancing === 0 && (
                    <p className="text-xs text-gray-400 italic">{t('Aucune activite de financement', 'No financing activity')}</p>
                  )}
                  <div className="border-t-2 border-blue-500/20 pt-2 flex justify-between font-bold">
                    <span className="text-blue-400">{t('Net Financement', 'Net Financing')}</span>
                    <span className="text-blue-400">{formatMoney(yr.netFinancing, language, 0)}</span>
                  </div>
                </div>

                {/* Year Net Cash Flow */}
                <div className={`rounded-lg p-4 border-2 ${yr.netCashFlow >= 0 ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-white font-bold">{t('Flux de tresorerie net', 'Net Cash Flow')} — {t('Annee', 'Year')} {yr.year}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {formatMoney(yr.netOperating, language, 0)} + ({formatMoney(yr.netMarketing, language, 0)}) + {formatMoney(yr.netFinancing, language, 0)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className={`text-2xl font-bold ${yr.netCashFlow >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {formatMoney(yr.netCashFlow, language, 0)}
                      </p>
                      <p className="text-xs text-gray-500">{t('Cumul', 'Cumul.')}: {formatMoney(yr.cumulativeCash, language, 0)}</p>
                    </div>
                  </div>
                </div>
              </div>
              )}
            </div>
            );
          })}

          {/* Grand Total */}
          <div className={`rounded-xl p-5 border-2 ${results.totalNetCash >= 0 ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
            <div className="flex justify-between items-center">
              <div>
                <p className="text-white font-bold text-lg">{t('Flux de tresorerie net total', 'Total Net Cash Flow')}</p>
                <p className="text-xs text-gray-500 mt-1">{results.projectionYears} {t('an(s)', 'year(s)')}</p>
              </div>
              <div className="text-right">
                <p className={`text-3xl font-bold ${results.totalNetCash >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {formatMoney(results.totalNetCash, language, 0)}
                </p>
              </div>
            </div>
          </div>

          {/* PDF Export Bottom */}
          <div className="text-center">
            <button onClick={exportPDF}
              className="inline-flex items-center gap-2 px-8 py-3 bg-red-600 hover:bg-red-500 text-white font-medium rounded-xl transition-colors shadow-lg shadow-red-500/20"
              data-testid="cf-export-pdf-bottom">
              <FilePdf className="w-5 h-5" />
              {t('Telecharger le Cash Flow Statement (PDF)', 'Download Cash Flow Statement (PDF)')}
            </button>
          </div>
        </div>
        )}
      </div>
    </OrbitLayout>
  );
};

export default OrbitCashFlowPage;
